import React, { useState } from 'react'
import { ChevronDownIcon, ChevronRightIcon, ExternalLinkIcon, User, FileText, Target, BarChart3, Shield, MoreHorizontal, ArrowRight, Users } from 'lucide-react'
import { getSemanticConfig, getDataType, shouldGroupArrayItems, getPriorityOrder, RenderingConfig } from '../utils/dataRenderer'
import { formatDateSafely } from '../utils/dateUtils'
import GoalRenderer from './GoalRenderer'
import AssessmentRenderer from './AssessmentRenderer'
import AccommodationRenderer from './AccommodationRenderer'
import ServiceRenderer from './ServiceRenderer'
import PresentLevelRenderer from './PresentLevelRenderer'
import { Goal } from '../types'

interface SmartDataRendererProps {
  data: any
  fieldName?: string
  level?: number
  onSourceClick?: (pageNumber: number) => void
  maxLevel?: number
}

export const SmartDataRenderer: React.FC<SmartDataRendererProps> = ({
  data,
  fieldName = 'data',
  level = 0,
  onSourceClick,
  maxLevel = 3
}) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set())
  const [activeTab, setActiveTab] = useState<string | null>(null)

  if (data === null || data === undefined) {
    return <span className="text-gray-400 italic">No data available</span>
  }

  const config = getSemanticConfig(fieldName, data)
  const dataType = getDataType(data)

  const toggleSection = (sectionKey: string) => {
    const newExpanded = new Set(expandedSections)
    if (newExpanded.has(sectionKey)) {
      newExpanded.delete(sectionKey)
    } else {
      newExpanded.add(sectionKey)
    }
    setExpandedSections(newExpanded)
  }

  const renderSourceReference = (pageNumber: number) => {
    if (!onSourceClick) return null
    return (
      <button
        onClick={(e) => {
          e.stopPropagation() // Prevent parent click handlers
          onSourceClick(pageNumber)
        }}
        className="ml-2 p-1 rounded hover:bg-gray-100 transition-colors text-gray-400 hover:text-blue-600"
        title={`Go to page ${pageNumber} in PDF`}
      >
        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
        </svg>
      </button>
    )
  }

  const renderPrimitiveValue = (value: any, config: RenderingConfig) => {

    if (config.component === 'email') {
      return (
        <a href={`mailto:${value}`} className="text-blue-600 hover:underline">
          {value}
        </a>
      )
    }

    if (config.component === 'url') {
      return (
        <a 
          href={value} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="text-blue-600 hover:underline inline-flex items-center"
        >
          {value} <ExternalLinkIcon className="h-3 w-3 ml-1" />
        </a>
      )
    }

    if (config.component === 'pageRef') {
      const pageNum = typeof value === 'number' ? value : parseInt(value, 10)
      if (!isNaN(pageNum)) {
        return (
          <div className="flex items-center">
            {renderSourceReference(pageNum)}
          </div>
        )
      }
    }

    if (config.component === 'longText' && typeof value === 'string' && value.length > 100) {
      return (
        <div className="prose prose-sm max-w-none">
          <p className="text-gray-700 leading-relaxed">{value}</p>
        </div>
      )
    }

    const formattedValue = config.formatter ? config.formatter(value) : String(value)

    return (
      <div className="text-gray-700">
        {formattedValue}
      </div>
    )
  }

  const renderArrayData = (arrayData: any[], config: RenderingConfig) => {
    if (arrayData.length === 0) {
      return (
        <div className="text-gray-500 italic p-4 bg-gray-50 rounded-lg">
          No {config.displayName?.toLowerCase()} found
        </div>
      )
    }

    // Special handling for goals - use custom GoalRenderer
    if (fieldName === 'goals' || fieldName.includes('goal')) {
      return (
        <div className="space-y-6">
          {arrayData.map((goal: Goal, index) => (
            <GoalRenderer 
              key={index}
              goal={goal}
              index={index}
              onSourceClick={onSourceClick}
            />
          ))}
        </div>
      )
    }

    // Special handling for assessments - use custom AssessmentRenderer
    if (fieldName === 'assessments' || fieldName.includes('assessment')) {
      return (
        <AssessmentRenderer 
          assessments={arrayData}
          onSourceClick={onSourceClick}
        />
      )
    }

    // Special handling for accommodations - use custom AccommodationRenderer
    if (fieldName === 'accommodations' || fieldName.includes('accommodation')) {
      return (
        <AccommodationRenderer 
          accommodations={arrayData}
          onSourceClick={onSourceClick}
        />
      )
    }

    // Special handling for services - use custom ServiceRenderer
    if (fieldName === 'services' || fieldName.includes('service')) {
      return (
        <ServiceRenderer 
          services={arrayData}
          onSourceClick={onSourceClick}
        />
      )
    }

    const shouldGroup = shouldGroupArrayItems(arrayData)

    if (shouldGroup && typeof arrayData[0] === 'object') {
      return (
        <div className="space-y-4">
          {arrayData.map((item, index) => {
            const sectionKey = `${fieldName}-${index}`
            const isExpanded = expandedSections.has(sectionKey) || level === 0
            
            // Try to get a meaningful title from the object
            const title = item.name || item.title || item.type || item.area || `Item ${index + 1}`
            const sourcePageNum = item.source_page && typeof item.source_page === 'number' ? item.source_page : null
            
            return (
              <div key={index} className="border border-gray-200 rounded-lg overflow-hidden bg-white mb-4">
                <div 
                  className="flex items-center justify-between p-5 bg-white cursor-pointer hover:bg-gray-50 border-b border-gray-100"
                  onClick={() => toggleSection(sectionKey)}
                >
                  <div className="flex items-center flex-1">
                    <h3 className="text-base font-medium text-gray-900 flex-1">{title}</h3>
                    {sourcePageNum && renderSourceReference(sourcePageNum)}
                  </div>
                  {isExpanded ? (
                    <ChevronDownIcon className="h-5 w-5 text-gray-400 ml-3" />
                  ) : (
                    <ChevronRightIcon className="h-5 w-5 text-gray-400 ml-3" />
                  )}
                </div>
                {isExpanded && (
                  <div className="p-5">
                    <SmartDataRenderer 
                      data={item} 
                      fieldName={`${fieldName}[${index}]`}
                      level={level + 1}
                      onSourceClick={onSourceClick}
                      maxLevel={maxLevel}
                    />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )
    }

    // Simple array rendering
    return (
      <div className="space-y-2">
        {arrayData.map((item, index) => (
          <div key={index} className="flex items-start">
            <div className="flex-shrink-0 flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 text-blue-600 mr-3 mt-0.5">
              <span className="text-xs font-medium">{index + 1}</span>
            </div>
            <div className="flex-1">
              <SmartDataRenderer 
                data={item} 
                fieldName={`${fieldName}[${index}]`}
                level={level + 1}
                onSourceClick={onSourceClick}
                maxLevel={maxLevel}
              />
            </div>
          </div>
        ))}
      </div>
    )
  }

  // Tab configuration mapping
  const getTabConfig = (key: string) => {
    const lowerKey = key.toLowerCase()
    
    if (lowerKey.includes('student') || lowerKey.includes('info')) {
      return { id: 'overview', label: 'Overview', icon: User, priority: 1 }
    }
    if (lowerKey.includes('present') || lowerKey.includes('level')) {
      return { id: 'present_levels', label: 'Present Levels', icon: FileText, priority: 2 }
    }
    if (lowerKey.includes('goal')) {
      return { id: 'goals', label: 'Goals', icon: Target, priority: 3 }
    }
    if (lowerKey.includes('assessment')) {
      return { id: 'assessments', label: 'Assessments', icon: BarChart3, priority: 4 }
    }
    if (lowerKey.includes('accommodation') || lowerKey.includes('service')) {
      return { id: 'accommodations_services', label: 'Accommodations & Services', icon: Shield, priority: 5 }
    }
    if (lowerKey.includes('transition')) {
      return { id: 'transition', label: 'Transition', icon: ArrowRight, priority: 6 }
    }
    // Team, disability, and student info now show in Overview instead of More
    if (lowerKey.includes('team') || lowerKey.includes('disability') || lowerKey.includes('student')) {
      return { id: 'overview', label: 'Overview', icon: User, priority: 1 }
    }
    
    return { id: 'more', label: 'More', icon: MoreHorizontal, priority: 7 }
  }
  
  const renderOverviewCards = (allEntries: any[]) => {
    // Find student info, team members, disabilities, and other relevant data
    const studentsFound = allEntries.find(({ key }) => key.toLowerCase().includes('students_found'))?.value || []
    const studentInfo = studentsFound[0]?.extracted_info || allEntries.find(({ key }) => key.toLowerCase().includes('student'))?.value || {}
    const teamMembers = allEntries.find(({ key }) => key.toLowerCase().includes('team'))?.value || []
    
    // Handle disabilities data - could be array, object with primary/secondary, or null
    const disabilityData = allEntries.find(({ key }) => key.toLowerCase().includes('disabilit'))?.value
    let disabilities = []
    
    if (Array.isArray(disabilityData)) {
      disabilities = disabilityData
    } else if (disabilityData && typeof disabilityData === 'object') {
      // Handle new format with primary_disability and secondary_disabilities
      if (disabilityData.primary_disability) {
        disabilities.push(disabilityData.primary_disability)
      }
      if (Array.isArray(disabilityData.secondary_disabilities)) {
        disabilities.push(...disabilityData.secondary_disabilities)
      }
    }
    
    const renderInfoCard = (title: string, icon: React.ComponentType<any>, content: React.ReactNode, sourcePageNum?: number) => {
      const IconComponent = icon
      
      return (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-blue-100 text-blue-600 mr-4">
                  <IconComponent className="h-5 w-5" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
              </div>
              {sourcePageNum && renderSourceReference(sourcePageNum)}
            </div>
          </div>
          <div className="px-6 py-5">
            {content}
          </div>
        </div>
      )
    }
    
    const renderStudentCard = () => {
      if (!studentInfo || Object.keys(studentInfo).length === 0) return null
      
      // Support both new student identification format and legacy format
      const name = studentInfo.full_name || 
                  (studentInfo.first_name && studentInfo.last_name ? 
                   `${studentInfo.first_name} ${studentInfo.last_name}` : 
                   studentInfo.name)
      
      return renderInfoCard(
        "Student Information",
        User,
        <div className="space-y-3">
          {name && (
            <div>
              <span className="font-medium text-gray-700">Name:</span>
              <span className="ml-2 text-gray-900">{name}</span>
            </div>
          )}
          {studentInfo.date_of_birth && (
            <div>
              <span className="font-medium text-gray-700">Date of Birth:</span>
              <span className="ml-2 text-gray-900">{formatDateSafely(studentInfo.date_of_birth, { format: 'numeric' })}</span>
            </div>
          )}
          {(studentInfo.grade_level || studentInfo.grade) && (
            <div>
              <span className="font-medium text-gray-700">Grade:</span>
              <span className="ml-2 text-gray-900">{studentInfo.grade_level || studentInfo.grade}</span>
            </div>
          )}
          {(studentInfo.school_name || studentInfo.school) && (
            <div>
              <span className="font-medium text-gray-700">School:</span>
              <span className="ml-2 text-gray-900">{studentInfo.school_name || studentInfo.school}</span>
            </div>
          )}
          {studentInfo.district && (
            <div>
              <span className="font-medium text-gray-700">District:</span>
              <span className="ml-2 text-gray-900">{studentInfo.district}</span>
            </div>
          )}
          {(studentInfo.student_id || studentInfo.id) && (
            <div>
              <span className="font-medium text-gray-700">Student ID:</span>
              <span className="ml-2 text-gray-900">{studentInfo.student_id || studentInfo.id}</span>
            </div>
          )}
          {studentInfo.iep_date && (
            <div>
              <span className="font-medium text-gray-700">IEP Date:</span>
              <span className="ml-2 text-gray-900">{formatDateSafely(studentInfo.iep_date, { format: 'numeric' })}</span>
            </div>
          )}
        </div>,
        studentInfo.source_page
      )
    }
    
    const renderEligibilityCard = () => {
      if (!disabilities || !Array.isArray(disabilities) || disabilities.length === 0) return null
      
      return renderInfoCard(
        "Disabilities",
        Shield,
        <div className="space-y-4">
          {disabilities.map((disability: any, index: number) => {
            // Handle both new and legacy disability format
            const category = disability.category || disability.type
            const description = disability.supporting_text || disability.description
            const shortDesc = disability.short_description
            
            return (
              <div key={index} className="border-l-4 border-blue-200 pl-4">
                <div className="font-semibold text-gray-900 mb-2">
                  {category || `Disability ${index + 1}`}
                  {index === 0 && <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Primary</span>}
                  {index > 0 && <span className="ml-2 text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">Secondary</span>}
                </div>
                {shortDesc && (
                  <div className="text-gray-600 text-sm mb-2 italic">
                    {shortDesc}
                  </div>
                )}
                {description && (
                  <div className="text-gray-700 text-sm mb-2 leading-relaxed">
                    {description}
                  </div>
                )}
                {disability.source_page && (
                  <div className="flex items-center">
                    {renderSourceReference(disability.source_page)}
                  </div>
                )}
              </div>
            )
          })}
        </div>,
        disabilities[0]?.source_page
      )
    }
    
    const renderTeamCard = () => {
      if (!teamMembers || teamMembers.length === 0) return null
      
      return renderInfoCard(
        "IEP Team",
        Users,
        <div className="space-y-2">
          {teamMembers.map((member: any, index: number) => (
            <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0">
              <div>
                <div className="font-medium text-gray-900">{member.name || 'Unnamed Team Member'}</div>
                <div className="text-sm text-gray-600">
                  {member.roles && Array.isArray(member.roles) && member.roles.length > 0 
                    ? member.roles.join(', ') 
                    : member.role || member.title || 'Role not specified'
                  }
                </div>
              </div>
              {member.organization && (
                <div className="text-xs text-gray-500">{member.organization}</div>
              )}
            </div>
          ))}
        </div>,
        teamMembers[0]?.source_page
      )
    }
    
    // Use a container-responsive grid that wraps based on available width
    // repeat(auto-fit, minmax(260px, 1fr)) ensures cards flow without squishing
    const gridClasses = 'grid gap-6 [grid-template-columns:repeat(auto-fit,minmax(260px,1fr))]'

    return (
      <div className={gridClasses}>
        {renderStudentCard()}
        {renderEligibilityCard()}
        {renderTeamCard()}
      </div>
    )
  }
  
  const renderTabbedInterface = (sortedEntries: any[]) => {
    // Group entries by tab
    const tabGroups: { [key: string]: { config: any, entries: any[] } } = {}
    
    sortedEntries.forEach(({ key, value, config }) => {
      // Skip empty data
      if (!value || (Array.isArray(value) && value.length === 0)) return
      
      const tabConfig = getTabConfig(key)
      if (!tabGroups[tabConfig.id]) {
        tabGroups[tabConfig.id] = {
          config: tabConfig,
          entries: []
        }
      }
      tabGroups[tabConfig.id].entries.push({ key, value, config })
    })
    
    // Get available tabs sorted by priority
    const availableTabs = Object.values(tabGroups)
      .sort((a, b) => a.config.priority - b.config.priority)
    
    // Set first available tab as active if none selected
    if (!activeTab && availableTabs.length > 0) {
      setActiveTab(availableTabs[0].config.id)
    }
    
    const currentTabData = activeTab ? tabGroups[activeTab] : null
    
    return (
      <div className="w-full">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-6">
          <div className="flex space-x-8">
            {availableTabs.map(({ config: tabConfig }) => {
              const IconComponent = tabConfig.icon
              const isActive = activeTab === tabConfig.id
              
              return (
                <button
                  key={tabConfig.id}
                  onClick={() => setActiveTab(tabConfig.id)}
                  className={`flex items-center px-1 py-4 border-b-2 font-medium text-sm transition-colors ${
                    isActive
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <IconComponent className="h-5 w-5 mr-2" />
                  {tabConfig.label}
                </button>
              )
            })}
          </div>
        </div>
        
        {/* Tab Content */}
        {currentTabData && (
          <div className="space-y-8">
            {activeTab === 'overview' ? (
              renderOverviewCards(sortedEntries)
            ) : (
              currentTabData.entries.map(({ key, value, config }) => (
                <div key={key}>
                  {/* Skip header for present_levels since it has its own header */}
                  {!key.includes('present_level') && (
                    <div className="mb-6">
                      <h2 className="text-2xl font-bold text-gray-900 mb-1">
                        {config.displayName}
                      </h2>
                    </div>
                  )}
                  <SmartDataRenderer 
                    data={value} 
                    fieldName={key}
                    level={level + 1}
                    onSourceClick={onSourceClick}
                    maxLevel={maxLevel}
                  />
                </div>
              ))
            )}
          </div>
        )}
      </div>
    )
  }

  const renderObjectData = (objectData: any) => {
    const entries = Object.entries(objectData)
    if (entries.length === 0) {
      return <div className="text-gray-500 italic">Empty object</div>
    }

    // Special handling for present_levels - use custom PresentLevelRenderer
    if (fieldName === 'present_levels' || fieldName.includes('present_level')) {
      return (
        <PresentLevelRenderer 
          present_levels={objectData}
          onSourceClick={onSourceClick}
        />
      )
    }

    // Get configurations for all fields and sort by priority
    const fieldConfigs = entries.map(([key, value]) => ({
      key,
      value,
      config: getSemanticConfig(key, value)
    }))

    const sortedConfigs = getPriorityOrder(fieldConfigs.map(f => f.config))
    const sortedEntries = sortedConfigs.map(config => 
      fieldConfigs.find(f => f.config.fieldName === config.fieldName)
    ).filter(Boolean)

    if (level >= maxLevel) {
      return (
        <div className="text-gray-500 italic">
          [Object with {entries.length} properties - max depth reached]
        </div>
      )
    }

    // Check if this is a top-level section that should get tabbed interface
    const isTopLevelSection = level === 0 && fieldName === 'analysis'
    
    if (isTopLevelSection) {
      return renderTabbedInterface(sortedEntries)
    }

    return (
      <div className="space-y-4">
        {sortedEntries.filter(entry => entry !== undefined).map(({ key, value, config }) => (
          <div key={key}>
            <div className="mb-3">
              <h4 className="text-lg font-medium text-gray-800">
                {config.displayName}
              </h4>
            </div>
            <SmartDataRenderer 
              data={value} 
              fieldName={key}
              level={level + 1}
              onSourceClick={onSourceClick}
              maxLevel={maxLevel}
            />
          </div>
        ))}
      </div>
    )
  }

  // Main rendering logic
  if (dataType === 'array') {
    return renderArrayData(data as any[], config)
  }

  if (dataType === 'object') {
    return renderObjectData(data)
  }

  // Primitive values
  return renderPrimitiveValue(data, config)
}

export default SmartDataRenderer